import React, { useState, useEffect } from "react";
import { X, Users, Save, ChevronDown, ChevronUp, Info } from "lucide-react";
import { WIE_SKILL_KEYS as ALL_SKILLS, WIE_SKILL_LABELS as SKILL_LABELS } from "../../constants/wieSkills";

const DEFAULT_ROW = () => ({
  currentFte: "",
  contractorHours: "",
  outsourcedHours: "",
  plannedHires: "",
  loadedAnnualCost: "",
});

function NumField({ label, value, onChange, placeholder = "0", prefix }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">{prefix}</span>
        )}
        <input
          type="number"
          min="0"
          step="0.1"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full border border-gray-200 rounded-lg py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 ${prefix ? "pl-6 pr-2" : "px-3"}`}
        />
      </div>
    </div>
  );
}

const AMF_OPTIONS = [
  { value: "Low",    label: "Low (0.25×)",    desc: "Manual processes, basic tooling" },
  { value: "Medium", label: "Medium (0.15×)", desc: "Partial automation, some playbooks" },
  { value: "High",   label: "High (0.10×)",   desc: "Mature SOC, automated triage — Axaris default" },
  { value: "Elite",  label: "Elite (0.06×)",  desc: "AI-assisted, near-full automation" },
];

export default function WieHeadcountSetup({ capacityData, onSave, onClose, saving, settings }) {
  const [rows, setRows] = useState({});
  const [expanded, setExpanded] = useState({});
  const [automationMaturity, setAutomationMaturity]           = useState(settings?.automationMaturity ?? "High");
  const [historicalValidation, setHistoricalValidation]       = useState(settings?.historicalValidationCertified ?? false);

  // Initialise from loaded capacity data
  useEffect(() => {
    const init = {};
    ALL_SKILLS.forEach(skill => {
      const cap = capacityData?.find(c => c.skillArea === skill);
      init[skill] = {
        currentFte:      cap?.currentFte      != null ? String(cap.currentFte)      : "",
        contractorHours: cap?.contractorHours  != null ? String(cap.contractorHours)  : "",
        outsourcedHours: cap?.outsourcedHours  != null ? String(cap.outsourcedHours)  : "",
        plannedHires:    cap?.plannedHires     != null ? String(cap.plannedHires)     : "",
        loadedAnnualCost:cap?.loadedAnnualCost != null ? String(cap.loadedAnnualCost) : "",
      };
    });
    setRows(init);
  }, [capacityData]);

  const update = (skill, field, val) =>
    setRows(prev => ({ ...prev, [skill]: { ...prev[skill], [field]: val } }));

  const toggle = (skill) =>
    setExpanded(prev => ({ ...prev, [skill]: !prev[skill] }));

  // Safe number parse: treats empty/null as 0 but preserves an explicit 0 entry.
  // Avoids the `parseFloat("0") || 0` pitfall where 0 is falsy and gets swallowed.
  const n = (v) => { const p = parseFloat(v); return isNaN(p) ? 0 : p; };

  const handleSave = () => {
    const skills = ALL_SKILLS.map(skill => ({
      skillArea:        skill,
      currentFte:       n(rows[skill]?.currentFte),
      contractorHours:  n(rows[skill]?.contractorHours),
      outsourcedHours:  n(rows[skill]?.outsourcedHours),
      plannedHires:     n(rows[skill]?.plannedHires),
      loadedAnnualCost: n(rows[skill]?.loadedAnnualCost),
    }));
    onSave(skills, { automationMaturity, historicalValidationCertified: historicalValidation });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Team Capacity Setup</h2>
              <p className="text-xs text-gray-500">Enter current headcount to enable Gap Analysis mode</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info banner */}
        <div className="mx-6 mt-4 mb-2 flex gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3 flex-shrink-0">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 leading-relaxed">
            Enter your current security team headcount per skill area. Leave fields empty for areas with no dedicated staff.
            Contractor hours and outsourced hours are added to available capacity using a 109h/FTE/month rate.
          </p>
        </div>

        {/* Model Settings */}
        <div className="mx-6 mb-3 border border-gray-200 rounded-xl overflow-hidden flex-shrink-0">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-700">Workforce Model Settings</span>
          </div>
          <div className="px-4 py-3 space-y-3">
            {/* AMF */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Automation Maturity Level
                <span className="ml-1 text-gray-400 font-normal">(affects FTE calculation)</span>
              </label>
              <select
                value={automationMaturity}
                onChange={e => setAutomationMaturity(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
              >
                {AMF_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label} — {o.desc}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">
                Scales analyst hours per finding. High (0.10×) is the Axaris default for mature enterprise teams.
              </p>
            </div>
            {/* Historical validation */}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={historicalValidation}
                onChange={e => setHistoricalValidation(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
              />
              <div>
                <span className="text-xs font-medium text-gray-700">
                  Historical validation confirmed
                </span>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  We have compared forecast outputs against actual remediation times or analyst effort data.
                  Self-certified — supporting evidence should be retained internally.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Skill rows */}
        <div className="overflow-y-auto flex-1 px-6 pb-4 space-y-2">
          {ALL_SKILLS.map(skill => {
            const row = rows[skill] || DEFAULT_ROW();
            const isOpen = expanded[skill];
            const hasFte = parseFloat(row.currentFte) > 0;
            const hasExtra = parseFloat(row.contractorHours) > 0 || parseFloat(row.outsourcedHours) > 0;

            return (
              <div key={skill} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Row header */}
                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-800">{SKILL_LABELS[skill]}</span>
                      {(hasFte || hasExtra) && (
                        <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                          {hasFte ? `${row.currentFte} FTE` : ""}
                          {hasFte && hasExtra ? " +" : ""}
                          {hasExtra ? " extras" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Quick FTE input */}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={row.currentFte}
                      onChange={e => update(skill, "currentFte", e.target.value)}
                      placeholder="FTE"
                      className="w-20 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                    />
                    <button
                      onClick={() => toggle(skill)}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-200 transition-colors"
                      title={isOpen ? "Collapse" : "Expand for more options"}
                    >
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="px-4 py-3 border-t border-gray-100 bg-white">
                    <div className="grid grid-cols-2 gap-3">
                      <NumField
                        label="Contractor Hours/Month"
                        value={row.contractorHours}
                        onChange={v => update(skill, "contractorHours", v)}
                      />
                      <NumField
                        label="Outsourced Hours/Month"
                        value={row.outsourcedHours}
                        onChange={v => update(skill, "outsourcedHours", v)}
                      />
                      <NumField
                        label="Planned Hires (next 90d)"
                        value={row.plannedHires}
                        onChange={v => update(skill, "plannedHires", v)}
                      />
                      <NumField
                        label="Loaded Annual Cost/FTE"
                        value={row.loadedAnnualCost}
                        onChange={v => update(skill, "loadedAnnualCost", v)}
                        prefix="$"
                        placeholder="150000"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-400">
            Data is saved per organization — not shared with other tenants.
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : "Save Capacity"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
